import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUser, useClerk } from '../contexts/TestAuthContext';
import { useBranding } from '../contexts/BrandingContext';
import { usePermissions, Permissions } from '../contexts/PermissionsContext';
import { api } from '../utils/api';
import {
  Home,
  Plus,
  FileText,
  Clock,
  FolderOpen,
  PieChart,
  Users,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  BarChart3,
  Scale,
  Menu,
  X,
  Briefcase,
  Bell,
  MessageSquare,
  HelpCircle,
  CheckCircle,
  AlertCircle,
  Archive,
  Sparkles,
  Brain,
  RefreshCw
} from 'lucide-react';
// WorkspaceShell integration - Phase 2.1
import WorkspaceShell from '../layouts/WorkspaceShell';
import { isFeatureEnabled } from '../config/featureFlags';
import { FigureCoWordmark, FigureCoMonogram } from './brand/FigureCoLogo';

export type NavRole = 'admin' | 'accountant' | 'data_specialist';

/**
 * Map the combination of navRole (set by RoleDashboardLayout) and the
 * backend permission role to the WorkspaceRole consumed by WorkspaceShell.
 *
 * navRole always wins.  When it is absent we fall back to the backend role
 * so that data-specialist and internal-accountant seats get the correct
 * rail (with Batch OCR, Work Desk → /workdesk/data-specialist, etc.).
 *
 * Edge (K3 O3): if /team/my-permissions fails, backendRole degrades to 'viewer' and
 * this returns the analyst rail while SeatRouteGuard still applies the seat policy
 * from workspaceRole. Today every analyst-rail item is allowed for every guarded
 * seat; keep it that way, or the rail will show links the guard bounces.
 */
export function resolveShellRole(
  navRole: NavRole | undefined,
  backendRole: string | null | undefined,
): import('../config/toolsManifest').WorkspaceRole {
  if (navRole === 'accountant') return 'accountant_internal';
  if (navRole === 'data_specialist') return 'data_specialist';
  if (navRole === 'admin') return 'admin';
  if (backendRole === 'data_specialist') return 'data_specialist';
  if (backendRole === 'accountant') return 'accountant_internal';
  if (backendRole === 'admin' || backendRole === 'owner') return 'admin';
  return 'analyst';
}

interface AppLayoutProps {
  children: React.ReactNode;
  navRole?: NavRole;
}

// Paths hidden per role (admin sees everything)
const NAV_HIDDEN: Record<NavRole, string[]> = {
  admin: [],
  accountant: ['/settings', '/queue/accountant', '/game-theory', '/settlement-intelligence'],
  data_specialist: [
    '/compare', '/analytics', '/vendor-intelligence',
    '/game-theory', '/settlement-intelligence',
    '/settings', '/queue/accountant', '/closed-cases',
  ],
};

interface NavItem {
  icon: React.ElementType;
  label: string;
  path: string;
  badge?: string | number;
  // Optional location.state to pass on navigate. Used by "+ New Claim" to
  // signal `{ forceNew: true }` so wizard's useClaimId skips its
  // resume-via-/drafts/latest hijack and creates a fresh draft.
  state?: any;
}

export default function AppLayout({ children, navRole }: AppLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const { branding } = useBranding();
  const { hasPermission, role, roleDisplay, roleColor, loading: permissionsLoading } = usePermissions();

  // WorkspaceShell feature flag check - Phase 2.1
  // When enabled, use the new unified layout instead of legacy layout
  const env = (import.meta as any).env;
  const workspaceShellEnabled = isFeatureEnabled('WORKSPACE_SHELL') ||
    env?.VITE_WORKSPACE_SHELL === 'true' ||
    env?.VITE_WORKSPACE_SHELL === '1';

  if (workspaceShellEnabled) {
    // Map NavRole + backend role to WorkspaceRole.
    const workspaceRole = resolveShellRole(navRole, role);

    return (
      <WorkspaceShell
        userRole={workspaceRole}
        userName={user?.firstName || user?.primaryEmailAddress?.emailAddress?.split('@')[0] || 'User'}
        userEmail={user?.primaryEmailAddress?.emailAddress || ''}
        onSignOut={() => signOut()}
        onSettings={() => navigate('/settings')}
      >
        {children}
      </WorkspaceShell>
    );
  }

  // Legacy layout below - used when WorkspaceShell flag is off (safe rollback)
  
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Daylight/Dark theme toggle — flips data-theme on <html>, persisted.
  const [theme, setThemeState] = useState<'light' | 'dark'>(() => {
    if (typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark') return 'dark';
    return 'light';
  });
  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    document.documentElement.setAttribute('data-theme', next);
    try { localStorage.setItem('cs-theme', next); } catch { /* ignore */ }
    setThemeState(next);
  };
  const [pendingCount, setPendingCount] = useState(0);
  const [totalClaims, setTotalClaims] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notificationDropdownOpen, setNotificationDropdownOpen] = useState(false);
  
  // AI Suggestions state
  const [aiSuggestionsCount, setAiSuggestionsCount] = useState(0);
  const [aiSuggestions, setAiSuggestions] = useState<any[]>([]);
  const [aiDropdownOpen, setAiDropdownOpen] = useState(false);
  const [aiNeedsParsingCount, setAiNeedsParsingCount] = useState(0);
  const [parsingAll, setParsingAll] = useState(false);

  // Check permissions
  const canCreateClaims = hasPermission(Permissions.CLAIMS_CREATE);
  const canDeleteClaims = hasPermission(Permissions.CLAIMS_DELETE);
  const canExport = hasPermission(Permissions.REPORTS_EXPORT);

  // Fetch counts for badges from API
  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=500', { showErrorToast: false });
        const reports = Array.isArray(data.reports) ? data.reports : Array.isArray(data) ? data : [];
        setTotalClaims(reports.length);
        // Count pending (draft + in_progress)
        const pending = reports.filter((r: any) => r.status === 'draft' || r.status === 'in_progress').length;
        setPendingCount(pending);
        
        // Generate notifications from recent activity
        const recentNotifications: any[] = [];
        
        // Check for recently completed reports (last 24 hours)
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        reports.forEach((report: any) => {
          const updatedAt = new Date(report.updated_at || report.created_at);
          if (updatedAt > yesterday) {
            if (report.status === 'completed') {
              recentNotifications.push({
                id: `complete-${report.id}`,
                type: 'completed',
                title: 'Report Completed',
                message: `${report.client_name} calculation finished`,
                time: updatedAt,
                reportId: report.id
              });
            }
          }
        });
        
        // Sort by time and take most recent
        recentNotifications.sort((a, b) => b.time - a.time);
        setNotifications(recentNotifications.slice(0, 5));
        setNotificationCount(recentNotifications.length);
      } catch (error) {
        console.error('Error fetching sidebar counts:', error);
      }
    };
    fetchCounts();
  }, []);
  
  // Fetch AI suggestions count (poll every 60s)
  useEffect(() => {
    const fetchAiSuggestions = async () => {
      try {
        const data = await api.get('/api/v1/clarifications/pending-ai-suggestions?limit=10', { showErrorToast: false });
        const approvalItems = data.needs_approval || [];
        const parsingItems = data.needs_parsing || [];
        setAiSuggestionsCount(approvalItems.length);
        setAiSuggestions(approvalItems.slice(0, 5)); // Show top 5
        setAiNeedsParsingCount(parsingItems.length);
      } catch (error) {
        console.error('Error fetching AI suggestions:', error);
      }
    };
    
    fetchAiSuggestions();
    const interval = setInterval(fetchAiSuggestions, 60000); // Poll every 60s
    return () => clearInterval(interval);
  }, []);

  // Handle parse all from header dropdown
  const handleParseAllFromHeader = async () => {
    if (parsingAll) return;
    setParsingAll(true);
    
    try {
      await api.post('/api/v1/assistant/actions/parse-all-pending', null, { showErrorToast: false });
      
      // Refresh the data
      const data = await api.get('/api/v1/clarifications/pending-ai-suggestions?limit=10', { showErrorToast: false });
      setAiSuggestionsCount((data.needs_approval || []).length);
      setAiSuggestions((data.needs_approval || []).slice(0, 5));
      setAiNeedsParsingCount((data.needs_parsing || []).length);
    } catch (error) {
      console.error('Parse all error:', error);
    } finally {
      setParsingAll(false);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-dropdown]')) {
        setAiDropdownOpen(false);
        setNotificationDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Build nav items based on permissions
  // Pending Review is role-aware: for accountant + data_specialist roles,
  // it deep-links into the role dashboard's queue tab (the only "review"
  // surface that's actually relevant for them). For admin/owner the
  // legacy home-filter path is preserved. See
  // docs/plans/2026-04-25-accountant-queue-cleanup.md (BUG-2).
  const pendingReviewPath =
    navRole === 'accountant' || navRole === 'data_specialist'
      ? '/dashboard/accountant?tab=queue'
      : '/?filter=pending';

  const mainNavItems: NavItem[] = [
    { icon: Home, label: 'Dashboard', path: '/', badge: totalClaims },
    // Only show New Claim if user can create claims
    ...(canCreateClaims ? [{ icon: Plus, label: 'New Claim', path: '/wizard-v2', state: { forceNew: true } }] : []),
    { icon: Clock, label: 'Pending Review', path: pendingReviewPath, badge: pendingCount },
  ];

  const toolsNavItems: NavItem[] = [
    // Data Cleanup deprecated - features now in Wizard Step 3
    // { icon: FolderOpen, label: 'Data Cleanup', path: '/cleanup' },
    { icon: AlertCircle, label: 'Issue Tracker', path: '/issues' },
    { icon: Scale, label: 'Compare Claims', path: '/compare' },
    { icon: PieChart, label: 'Analytics', path: '/analytics' },
    { icon: Users, label: 'Vendor Intel', path: '/vendor-intelligence' },
    { icon: Brain, label: 'Game Theory', path: '/game-theory' },
    { icon: BarChart3, label: 'Settlement Intel', path: '/settlement-intelligence' },
    { icon: Settings, label: 'Settings', path: '/settings' },
  ];

  const resourceNavItems: NavItem[] = [
    { icon: FileText, label: 'Docs Guide', path: '/docs-guide' },
    { icon: Briefcase, label: 'Accountant Portal', path: '/queue/accountant' },
  ];

  const dataNavItems: NavItem[] = [
    { icon: Archive, label: 'Closed Cases', path: '/closed-cases' },
    { icon: HelpCircle, label: 'Review Queue', path: '/review-queue' },
  ];

  // Role-based nav filtering (when navRole is provided by RoleDashboardLayout)
  const hidden = navRole ? NAV_HIDDEN[navRole] : [];
  const filterNav = (items: NavItem[]) =>
    hidden.length > 0 ? items.filter((item) => !hidden.includes(item.path)) : items;

  const filteredToolsNav = filterNav(toolsNavItems);
  const filteredResourceNav = filterNav(resourceNavItems);
  const filteredDataNav = filterNav(dataNavItems);

  const isActive = (path: string) => {
    // Handle filter paths
    if (path === '/?filter=pending') {
      return location.search.includes('filter=pending');
    }
    // Pending Review for accountant/data_specialist resolves to the queue tab.
    if (path === '/dashboard/accountant?tab=queue') {
      return location.pathname === '/dashboard/accountant'
        && location.search.includes('tab=queue');
    }
    // Dashboard is active when at / without pending filter
    if (path === '/') {
      return location.pathname === '/' && !location.search.includes('filter=pending');
    }
    return location.pathname.startsWith(path);
  };

  const NavItemButton = ({ item }: { item: NavItem }) => (
    <button
      onClick={() => {
        navigate(item.path, item.state ? { state: item.state } : undefined);
        setMobileMenuOpen(false);
      }}
      className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg text-sm transition-all ${
        isActive(item.path)
          ? 'bg-[var(--color-brand)] text-[var(--color-bg-base)] shadow-lg'
          : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-primary)]'
      }`}
    >
      <div className="flex items-center gap-3">
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" />
        {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
      </div>
      {!sidebarCollapsed && item.badge !== undefined && (
        <span className={`text-xs px-2 py-0.5 rounded-full ${
          isActive(item.path) ? 'bg-white/20' : 'bg-[var(--color-brand)] text-white'
        }`}>
          {item.badge}
        </span>
      )}
    </button>
  );

  const Sidebar = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={`
      ${mobile ? 'w-64' : sidebarCollapsed ? 'w-16' : 'w-60'}
      bg-[var(--color-bg-base)] text-[var(--color-text-primary)] transition-all duration-300 flex flex-col h-full border-r border-[var(--color-hairline)]
    `}>
      {/* Logo */}
      <div className="p-4 border-b border-[var(--color-hairline)]">
        <div
          className={`flex items-center cursor-pointer ${sidebarCollapsed && !mobile ? 'justify-center' : 'justify-start'}`}
          onClick={() => navigate('/')}
          title="Go to Dashboard"
        >
          {sidebarCollapsed && !mobile ? (
            <span className="flex items-center justify-center text-text-primary hover:opacity-90 transition-opacity">
              <FigureCoMonogram size={34} />
            </span>
          ) : (
            <div className="flex items-center hover:opacity-90 transition-opacity">
              <FigureCoWordmark size="md" />
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-2">
        {/* Main Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Main
          </p>
        )}
        <div className="space-y-1">
          {mainNavItems.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Tools Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Tools
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredToolsNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Resources Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Resources
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredResourceNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>

        {/* Data Section */}
        {(!sidebarCollapsed || mobile) && (
          <p className="px-3 py-2 mt-6 text-xs font-semibold text-[var(--color-text-muted)] uppercase tracking-wider">
            Data
          </p>
        )}
        {sidebarCollapsed && !mobile && <div className="h-4" />}
        <div className="space-y-1">
          {filteredDataNav.map((item) => (
            <NavItemButton key={item.path} item={item} />
          ))}
        </div>
      </nav>

      {/* User Section */}
      {(!sidebarCollapsed || mobile) && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-[var(--color-bg-hover)] cursor-pointer">
            <div className="w-8 h-8 rounded-full bg-[var(--color-brand)] flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-bold">
                {user?.firstName?.[0] || user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                {user?.firstName || 'User'}
              </p>
              <p className="text-xs text-[var(--color-text-muted)] truncate">
                {user?.primaryEmailAddress?.emailAddress}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Collapse Toggle - Desktop only */}
      {!mobile && (
        <div className="p-3 border-t border-[var(--color-hairline)]">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-[var(--color-text-muted)] hover:bg-[var(--color-bg-hover)] hover:text-[var(--color-text-secondary)] rounded-lg transition"
          >
            {sidebarCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="text-sm">Collapse</span>
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] flex">
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex flex-shrink-0">
        <Sidebar />
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar mobile />
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="absolute top-4 right-4 p-2 text-white bg-black/50 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Bar */}
        <header className="bg-[var(--color-bg-surface)] border-b border-[var(--color-border)] px-4 lg:px-6 py-3 flex items-center justify-between sticky top-0 z-40">
          {/* Left: Mobile menu + Page title area */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] rounded-lg"
            >
              <Menu className="w-5 h-5" />
            </button>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to Daylight' : 'Switch to Dark'}
              aria-label={theme === 'dark' ? 'Switch to Daylight theme' : 'Switch to Dark theme'}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] hover:border-[var(--color-brand)] transition-colors text-[15px]"
            >
              {theme === 'dark' ? '☀' : '☾'}
            </button>
            {/* AI Suggestions Badge */}
            {aiSuggestionsCount > 0 && (
              <div className="relative" data-dropdown>
                <button
                  onClick={() => {
                    setAiDropdownOpen(!aiDropdownOpen);
                    setNotificationDropdownOpen(false);
                  }}
                  className="relative p-2 text-[var(--color-brand)] hover:text-[var(--color-brand-hover)] hover:bg-[var(--color-needs-human-tint)] rounded-lg transition-colors"
                  aria-label={`${aiSuggestionsCount} pending AI category suggestions`}
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                    <circle cx="7.5" cy="14.5" r="1.5" />
                    <circle cx="16.5" cy="14.5" r="1.5" />
                  </svg>
                  <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-xs font-bold text-white bg-[var(--color-brand)] rounded-full">
                    {aiSuggestionsCount > 9 ? '9+' : aiSuggestionsCount}
                  </span>
                </button>
                
                {/* AI Suggestions Dropdown */}
                {aiDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-96 bg-[var(--color-bg-surface)] rounded-xl shadow-lg border border-[var(--color-border)] z-50">
                    <div className="px-4 py-3 border-b border-[var(--color-border)] bg-[var(--color-needs-human-tint)]">
                      <h3 className="font-serif font-semibold text-[var(--color-text-primary)] flex items-center gap-2">
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" />
                        </svg>
                        AI Category Suggestions
                      </h3>
                      <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        {aiSuggestionsCount} response{aiSuggestionsCount !== 1 ? 's' : ''} ready for approval
                      </p>
                    </div>
                    <div className="max-h-80 overflow-y-auto">
                      {aiSuggestions.map((item, idx) => (
                        <button
                          key={item.transaction_id || idx}
                          onClick={() => {
                            navigate(`/wizard-v2?claim=${item.claim_id}&step=3&section=transactions`);
                            setAiDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-[var(--color-bg-hover)] border-b border-[var(--color-border-faint)] text-left"
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--color-text-primary)] truncate">
                                {item.claimant_name || 'Unknown Claim'}
                              </p>
                              <p className="text-xs text-[var(--color-text-muted)] truncate mt-0.5">
                                {item.description || item.transaction_id}
                              </p>
                            </div>
                            {item.amount && (
                              <span className={`text-sm font-medium font-mono flex-shrink-0 ${
                                item.amount < 0 ? 'text-[var(--color-brand)]' : 'text-[var(--color-verified)]'
                              }`}>
                                ${Math.abs(item.amount).toLocaleString()}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="text-xs font-medium text-[var(--color-text-primary)] bg-[var(--color-needs-human-tint)] px-2 py-0.5 rounded-full">
                              {item.ai_parsed_category}
                            </span>
                            {item.ai_confidence && (
                              <span className={`text-xs px-1.5 py-0.5 rounded ${
                                item.ai_confidence >= 0.9 ? 'text-[var(--color-verified)] bg-[rgba(34,197,94,0.12)]' :
                                item.ai_confidence >= 0.7 ? 'text-[var(--color-text-muted)] bg-[var(--color-hairline)]' :
                                'text-[var(--color-brand)] bg-[rgba(255,102,0,0.12)]'
                              }`}>
                                <span
                                  className="inline-block w-1.5 h-1.5 rounded-full mr-1"
                                  style={{
                                    backgroundColor: item.ai_confidence >= 0.9
                                      ? 'var(--color-verified)'
                                      : item.ai_confidence >= 0.7
                                      ? 'var(--color-warning)'
                                      : 'var(--color-blocker)'
                                  }}
                                />
                                {Math.round(item.ai_confidence * 100)}%
                              </span>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                    <div className="px-4 py-2 border-t border-[var(--color-border)] bg-[var(--color-bg-hover)] flex items-center justify-between">
                      <button
                        onClick={() => {
                          navigate('/');
                          setAiDropdownOpen(false);
                        }}
                        className="text-sm text-[var(--color-brand)] hover:text-[var(--color-brand-hover)] font-medium"
                      >
                        View All on Dashboard →
                      </button>
                      {aiNeedsParsingCount > 0 && (
                        <button
                          onClick={handleParseAllFromHeader}
                          disabled={parsingAll}
                          className="text-xs px-2 py-1 bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] hover:bg-[var(--color-needs-human-tint-bd)] rounded-md font-medium flex items-center gap-1 disabled:opacity-50"
                        >
                          {parsingAll ? (
                            <>
                              <RefreshCw className="w-3 h-3 animate-spin" />
                              Parsing...
                            </>
                          ) : (
                            <>
                              <Sparkles className="w-3 h-3" />
                              Parse All ({aiNeedsParsingCount})
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Notifications */}
            <div className="relative" data-dropdown>
              <button 
                onClick={() => setNotificationDropdownOpen(!notificationDropdownOpen)}
                className="relative p-2 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-border-faint)] rounded-lg"
              >
                <Bell className="w-5 h-5" />
                {notificationCount > 0 && (
                  <span className="absolute top-1 right-1 w-2 h-2 bg-[var(--color-brand)] rounded-full" />
                )}
              </button>
              
              {/* Notification Dropdown */}
              {notificationDropdownOpen && (
                <div className="absolute right-0 mt-2 w-80 bg-[var(--color-bg-surface)] rounded-xl shadow-lg border border-[var(--color-border)] z-50">
                  <div className="px-4 py-3 border-b border-[var(--color-hairline)]">
                    <h3 className="font-serif font-semibold text-[var(--color-text-primary)]">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="w-8 h-8 text-[var(--color-text-ghost)] mx-auto mb-2" />
                        <p className="text-sm text-[var(--color-text-muted)]">No new notifications</p>
                      </div>
                    ) : (
                      notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => {
                            navigate(`/report/${notif.reportId}`);
                            setNotificationDropdownOpen(false);
                          }}
                          className="w-full px-4 py-3 hover:bg-[var(--color-bg-base)] border-b border-[var(--color-border-faint)] text-left flex items-start gap-3"
                        >
                          <div className={`p-1.5 rounded-full flex-shrink-0 ${
                            notif.type === 'completed' ? 'bg-transparent' :
                            notif.type === 'comment' ? 'bg-[var(--color-needs-human-tint)]' : 'bg-[var(--color-bg-elevated)]'
                          }`}>
                            {notif.type === 'completed' ? (
                              <CheckCircle className="w-4 h-4 text-[var(--color-verified)]" />
                            ) : notif.type === 'comment' ? (
                              <MessageSquare className="w-4 h-4 text-[var(--color-brand)]" />
                            ) : (
                              <AlertCircle className="w-4 h-4 text-[var(--color-brand)]" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[var(--color-text-primary)]">{notif.title}</p>
                            <p className="text-xs text-[var(--color-text-muted)] truncate">{notif.message}</p>
                            <p className="text-xs text-[var(--color-text-dim)] mt-1">
                              {new Date(notif.time).toLocaleDateString('en-US', { 
                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                              })}
                            </p>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                  {notifications.length > 0 && (
                    <div className="px-4 py-2 border-t border-[var(--color-hairline)]">
                      <button 
                        onClick={() => {
                          setNotifications([]);
                          setNotificationCount(0);
                          setNotificationDropdownOpen(false);
                        }}
                        className="text-xs text-[var(--color-brand)] hover:text-[var(--color-brand-hover)]"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* New Claim Button - Only show if user can create claims */}
            {canCreateClaims && (
              <button
                onClick={() => navigate('/wizard-v2', { state: { forceNew: true } })}
                className="hidden sm:flex items-center gap-2 px-4 py-2 rounded-lg bg-[var(--color-brand)] hover:bg-[var(--color-brand-hover)] text-[var(--color-bg-base)] text-sm font-medium shadow-lg hover:shadow-xl transition-all"
              >
                <Plus className="w-4 h-4" />
                New Claim
              </button>
            )}

            {/* Role Badge — hidden while permissions are loading to prevent "Viewer" flash */}
            {!permissionsLoading && (
              <div
                className="hidden md:flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]"
              >
                {roleDisplay}
              </div>
            )}

            {/* Sign Out */}
            <button
              onClick={() => signOut()}
              className="p-2 text-[var(--color-text-dim)] hover:text-[var(--color-text-secondary)] hover:bg-[var(--color-border-faint)] rounded-lg"
              title="Sign Out"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
